import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Input, Select, Button } from '@/components/ui';
import { useProjects } from '@/hooks/useProjects';
import { useCreateTask, useUpdateTask } from '@/hooks/useTasks';
import type { CreateTaskInput, Task } from '@aelita/shared';

export interface TaskFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  /** Pre-select a project when creating a new task */
  defaultProjectId?: string;
}

export function TaskFormModal({ isOpen, onClose, task, defaultProjectId }: TaskFormModalProps) {
  // When editing, we need all projects (not just active) to show the current project
  const { data: projectsData } = useProjects(task ? {} : { status: 'active' });
  const projects = projectsData?.data ?? [];
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const isEditing = !!task;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateTaskInput>({
    defaultValues: task
      ? {
          projectId: task.projectId,
          title: task.title,
          description: task.description ?? '',
          type: task.type,
          priority: task.priority,
          status: task.status,
          estimatedHours: task.estimatedHours ?? undefined,
          dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
        }
      : {
          projectId: defaultProjectId ?? '',
        },
  });

  // Re-apply form values once projects have loaded so the select can match the projectId option
  useEffect(() => {
    if (task && projects.length > 0) {
      reset({
        projectId: task.projectId,
        title: task.title,
        description: task.description ?? '',
        type: task.type,
        priority: task.priority,
        status: task.status,
        estimatedHours: task.estimatedHours ?? undefined,
        dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
      });
    }
  }, [task, projects, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateTaskInput) => {
    try {
      if (isEditing) {
        await updateTask.mutateAsync({
          id: task.id,
          data,
        });
      } else {
        await createTask.mutateAsync(data);
      }
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to save task:', error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Task' : 'Create New Task'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Select
          label="Project"
          {...register('projectId', { required: 'Project is required' })}
          options={projects.map((p: any) => ({ value: p.id, label: p.name }))}
          placeholder="Select a project"
          error={errors.projectId?.message}
          defaultValue={task?.projectId ?? defaultProjectId ?? ''}
          disabled={isEditing || !!defaultProjectId}
        />

        <Input
          label="Title"
          {...register('title', { required: 'Title is required' })}
          error={errors.title?.message}
          defaultValue={task?.title}
        />

        <div>
          <label className="label">Description</label>
          <textarea
            {...register('description')}
            className="input min-h-[80px]"
            placeholder="Task description (supports Markdown)"
            defaultValue={task?.description ?? ''}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Select
            label="Type"
            {...register('type')}
            options={[
              { value: 'task', label: 'Task' },
              { value: 'bug', label: 'Bug' },
              { value: 'story', label: 'Story' },
              { value: 'epic', label: 'Epic' },
              { value: 'subtask', label: 'Subtask' },
            ]}
            defaultValue={task?.type ?? 'task'}
          />
          <Select
            label="Priority"
            {...register('priority')}
            options={[
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'critical', label: 'Critical' },
            ]}
            defaultValue={task?.priority ?? 'medium'}
          />
          <Select
            label="Status"
            {...register('status')}
            options={[
              { value: 'backlog', label: 'Backlog' },
              { value: 'todo', label: 'To Do' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'review', label: 'In Review' },
              { value: 'done', label: 'Done' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
            defaultValue={task?.status ?? 'backlog'}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Estimated Hours"
            type="number"
            step="0.5"
            {...register('estimatedHours', { valueAsNumber: true })}
            defaultValue={task?.estimatedHours ?? ''}
          />
          <Input
            label="Due Date"
            type="date"
            {...register('dueDate', { valueAsDate: true })}
            defaultValue={task?.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : ''}
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={createTask.isPending || updateTask.isPending}>
            {isEditing ? 'Save Changes' : 'Create Task'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
